<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";
import { formatedDate2 } from "../../../utils/proposalValidation";

export default {
    name: "FPTemp6",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: ["proposal"],
    data() {
        return {
            footerImage: ""
        };
    },
    computed: {
        project() {
            return this.proposal.projects && this.proposal.projects.length > 0
                ? this.proposal.projects[0]
                : null;
        }
    },
    mounted() {
        this.generateGradients();
    },
    methods: {
        formatedDate2,
        generateGradients() {
            const fCanvas = document.createElement('canvas');
            fCanvas.width = 800;
            fCanvas.height = 400;
            const fCtx = fCanvas.getContext('2d');
            const fGrad = fCtx.createLinearGradient(0, 400, 0, 0);
            fGrad.addColorStop(0, 'rgba(0, 0, 0, 0.8)');
            fGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            fCtx.fillStyle = fGrad;
            fCtx.fillRect(0, 0, 800, 400);
            this.footerImage = fCanvas.toDataURL();
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        '--header-color': proposal && proposal.style && proposal.style.colorScheme && proposal.style.colorScheme.header,
        '--body-color': proposal && proposal.style && proposal.style.colorScheme && proposal.style.colorScheme.body,
        '--footer-color': proposal && proposal.style && proposal.style.colorScheme && proposal.style.colorScheme.footer,
        '--heading-color': proposal.style.heading.color,
        '--heading-size': proposal.style.heading.size + 'px',
        '--heading-font': proposal.style.heading.font,
        '--heading-bold': proposal.style.heading.bold ? 700 : 400,
        '--heading-italic': proposal.style.heading.italic ? 'italic' : 'normal',
        '--heading-underline': proposal.style.heading.underline ? 'underline' : 'none',
        '--label-color': proposal.style.label.color,
        '--label-size': proposal.style.label.size + 'px',
        '--label-font': proposal.style.label.font,
        '--label-bold': proposal.style.label.bold ? 700 : 400,
        '--label-italic': proposal.style.label.italic ? 'italic' : 'normal',
        '--label-underline': proposal.style.label.underline ? 'underline' : 'none',
        '--text-color': proposal.style.text.color,
        '--text-size': proposal.style.text.size + 'px',
        '--text-font': proposal.style.text.font,
        '--text-bold': proposal.style.text.bold ? 700 : 400,
        '--text-italic': proposal.style.text.italic ? 'italic' : 'normal',
        '--text-underline': proposal.style.text.underline ? 'underline' : 'none',
    }">
        <div class="frontPage">
            <!-- Left Pane (Content & Logos) -->
            <div class="left-pane">
                <!-- Top Logo Box -->
                <div class="logo-box photobox">
                    <PhotoBox :project="proposal" type="clientLogo" :bgColor="proposal && proposal.style && proposal.style.colorScheme && proposal.style.colorScheme.body"
                        objectFit="contain" />
                </div>

                <!-- Main Content -->
                <div class="content-box">
                    <h1 class="proposal-name">
                        <SpellCheckedText :value="proposal.name || ''" @input="val => proposal.name = val" />
                    </h1>

                    <div class="meta-info text-left">
                        <div class="address">
                            <p>
                                <SpellCheckedText :value="proposal.address || ''"
                                    @input="val => project.address = val" />
                            </p>
                            <p>
                                <SpellCheckedText :value="proposal.city || ''" @input="val => proposal.city = val" />
                                <span v-if="proposal.city && proposal.stateProvince">, </span>
                                <SpellCheckedText :value="proposal.stateProvince || ''"
                                    @input="val => proposal.stateProvince = val" />
                            </p>
                        </div>
                        <p class="date">
                            {{ formatedDate2(proposal.submissionDate) }}
                        </p>
                    </div>
                </div>

                <!-- Bottom Secondary Logo -->
                <div class="secondary-logo photobox">
                    <PhotoBox :project="proposal" type="logo" bgColor="transparent" objectFit="contain" />
                </div>
            </div>

            <!-- Right Pane (Photo) -->
            <div class="right-pane">
                <PhotoBox v-if="project" class="photobox" :project="project"
                    :bgColor="proposal.style.colorScheme && proposal.style.colorScheme.footer" />
                <div v-else class="image-placeholder" />

                <div v-if="project && footerImage" class="bottom-gradient">
                    <img :src="footerImage" class="w-full h-full" />
                </div>

                <!-- Bottom Right Caption -->
                <div class="photo-caption" v-if="project">
                    <p class="caption-name">
                        <SpellCheckedText :value="project.projectName || ''"
                            @input="val => project.projectName = val" />
                    </p>
                    <p class="caption-location">
                        <SpellCheckedText :value="project.city || ''" @input="val => project.city = val" />
                        <span>{{ project.city ? ', ' : '' }}</span>
                        <SpellCheckedText :value="project.stateProvince || ''"
                            @input="val => project.stateProvince = val" />
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: block;
    width: 296.83mm;
    height: 211mm;
    min-height: 211mm;
    position: relative;
    box-sizing: border-box;
    background: white;
    overflow: hidden;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .frontPage {
        display: flex;
        flex-direction: row;
        width: 100%;
        height: 100%;

        .left-pane {
            width: 44%;
            background: var(--header-color);
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding: 100px 60px 60px 60px;
            color: white;

            .logo-box {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                max-width: 250px;
                height: 90px;
                margin-bottom: 80px;

                .logo-placeholder {
                    width: 100%;
                    height: 100%;
                }
            }

            .content-box {
                flex: 1;

                .proposal-name {
                    color: var(--heading-color);
                    font-size: var(--heading-size);
                    font-family: var(--heading-font);
                    font-weight: var(--heading-bold);
                    font-style: var(--heading-italic);
                    text-decoration: var(--heading-underline);
                    margin-bottom: 20px;
                    line-height: 1.2;
                }

                .meta-info {
                    .address {
                        color: var(--label-color);
                        font-size: var(--label-size);
                        font-family: var(--label-font);
                        font-weight: var(--label-bold);
                        font-style: var(--label-italic);
                        text-decoration: var(--label-underline);
                        margin-bottom: 25px;

                        p {
                            margin: 0;
                        }
                    }

                    .date {
                        color: var(--text-color);
                        font-size: var(--text-size);
                        font-family: var(--text-font);
                        font-weight: var(--text-bold);
                        font-style: var(--text-italic);
                        text-decoration: var(--text-underline);
                        margin-bottom: 0px;
                    }
                }
            }

            .secondary-logo {
                width: 150px;
                height: 80px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-top: 20px;
            }
        }

        .right-pane {
            position: relative;
            flex: 1;
            position: relative;
            background: #eee;

            .image-placeholder {
                width: 100%;
                height: 100%;
                background: var(--footer-color);
            }

            .bottom-gradient {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 150px;
                z-index: 2;
                pointer-events: none;
            }

            .photo-caption {
                position: absolute;
                bottom: 25px;
                right: 30px;
                z-index: 50;
                text-align: right;
                color: white;
                font-size: 13px;

                p {
                    margin: 0;
                    font-weight: 600;
                }

                .caption-location {
                    font-weight: 400;
                    font-style: italic;
                    opacity: 0.9;
                }
            }
        }
    }
}
</style>
